/**
 * Image utility functions for event handling
 */

/**
 * Checks if an event has a valid image (either image_url or image data)
 * @param {Object} event - The event object to check
 * @returns {boolean} - True if the event has a valid image
 */
export const eventHasImage = (event) => {
  if (!event) return false;

  // Check for direct image URL
  if (event.image_url) return true;

  // Check for Binary.createFromBase64 format
  if (event.image && typeof event.image === 'string' &&
    event.image.includes('createFromBase64')) {
    return true;
  }

  // Check for image data object
  if (event.image && event.image.data) {
    // Make sure data isn't empty
    return !!event.image.data;
  }

  return false;
};

/**
 * Gets the appropriate image source for an event
 * @param {Object} event - The event object
 * @returns {string|null} - Image source URL or data URL or null if no image
 */
export const getEventImageSrc = (event) => {
  if (!event) return null;

  // Direct image URL takes precedence
  if (event.image_url) return event.image_url;

  // Handle Binary.createFromBase64 format stored as string
  if (event.image && typeof event.image === 'string') {
    // Check if it's a Binary.createFromBase64 format
    if (event.image.includes('createFromBase64')) {
      try {
        // Extract the base64 string from Binary.createFromBase64('base64string', 0)
        const base64Match = event.image.match(/createFromBase64\(['"](.+?)['"],\s*\d+\)/);
        if (base64Match && base64Match[1]) {
          return `data:image/jpeg;base64,${base64Match[1]}`;
        }
      } catch (e) {
        console.error('Failed to parse Binary.createFromBase64 image', e);
      }
    }
  }

  // If there's image data, create a data URL
  if (event.image && event.image.contentType && event.image.data) {
    return `data:${event.image.contentType};base64,${event.image.data}`;
  }

  return null;
};

/**
 * Gets a URL for an event image in various possible formats
 * @param {Object} event - The event object
 * @returns {string|null} - URL for the image or null if no image available
 */
export const getEventImageUrl = (event) => {
  if (!event) return null;

  // Direct image URL
  if (event.image_url) return event.image_url;

  // Handle Binary.createFromBase64 format stored as string
  if (event.image && typeof event.image === 'string') {
    // Check if it's a Binary.createFromBase64 format
    if (event.image.includes('createFromBase64')) {
      try {
        // Extract the base64 string from Binary.createFromBase64('base64string', 0)
        const base64Match = event.image.match(/createFromBase64\(['"](.+?)['"],\s*\d+\)/);
        if (base64Match && base64Match[1]) {
          return `data:image/jpeg;base64,${base64Match[1]}`;
        }
      } catch (e) {
        console.error('Failed to parse Binary.createFromBase64 image', e);
      }
    }
  }

  // Handle image object with data property
  if (event.image && event.image.data) {
    const contentType = event.image.contentType || 'image/jpeg';
    return `data:${contentType};base64,${event.image.data}`;
  }

  return null;
};

/**
 * Safely processes image data from various formats
 * @param {Object} event - The event object to process
 * @returns {Object} - The event with properly processed image data
 */
export const processEventImage = (event) => {
  if (!event) return event;

  // Clone the event to avoid mutation
  const processedEvent = { ...event };

  // Handle potential image data issues
  if (processedEvent.image) {
    // Make sure image data is properly formatted
    if (typeof processedEvent.image === 'string') {
      try {
        // Check if it's already a valid base64 string
        if (processedEvent.image.startsWith('data:image')) {
          // It's already a data URL, extract parts
          const parts = processedEvent.image.split(',');
          if (parts.length === 2) {
            const contentType = parts[0].split(':')[1].split(';')[0];
            processedEvent.image = {
              data: parts[1],
              contentType: contentType
            };
          }
        }
        // Check if it's Binary.createFromBase64 format
        else if (processedEvent.image.includes('createFromBase64')) {
          const base64Match = processedEvent.image.match(/createFromBase64\(['"](.+?)['"],\s*\d+\)/);
          if (base64Match && base64Match[1]) {
            processedEvent.image = {
              data: base64Match[1],
              contentType: 'image/jpeg' // Default content type for Binary format
            };
          } else {
            // If we can't extract the base64, set to null
            console.warn('Could not extract base64 data from Binary.createFromBase64');
            processedEvent.image = null;
          }
        } else {
          // Try parsing as JSON
          processedEvent.image = JSON.parse(processedEvent.image);
        }
      } catch (e) {
        console.error('Error processing image data:', e);
        // If parsing fails, create a structured object but mark as invalid
        processedEvent.image = null;
      }
    }

    // Validate image data
    // If image has only a path (file-on-disk format) but no base64 data, that's fine - image_url handles display
    if (processedEvent.image && (!processedEvent.image.data || processedEvent.image.data === '')) {
      if (!processedEvent.image.path) {
        // Truly empty, set to null
        processedEvent.image = null;
      }
      // else: path-based image, keep as-is; image_url will be used for display
    }

    // Ensure contentType exists
    if (processedEvent.image && !processedEvent.image.contentType && processedEvent.image.data) {
      processedEvent.image.contentType = 'image/jpeg';  // Default to jpeg if not specified
    }
  }

  return processedEvent;
};
